import React from 'react'

import { Table } from 'antd'

import { removeSuppressionList } from 'core'
import PagedList from 'core/application/commons/pagedList'
import { SuppressionListItemResponse } from 'core/application/csiDashboard/suppressionList/getSuppressionList'
import SuppressionReason from 'core/domain/suppressionList/suppressionReason'

import { formatDateOnlyTime, parseZoneFormat } from 'common/dateFormatters'

import { Button, Tag } from '../../common/components'
import { useCommandRequest } from '../../common/hooks'

import './styles.less'

interface EmailsTableProps {
  emails?: PagedList<SuppressionListItemResponse>
  loadingEmails: boolean
  onChange?: (filters: { pageNumber: number; pageSize: number; orderColumn: string; orderDescending: boolean }) => any
  onRemove?: () => any
}

const EmailsTable: React.FC<EmailsTableProps> = ({ emails, loadingEmails, onChange, onRemove }) => {
  const [sendCommand, isCommandLoading] = useCommandRequest()

  const onRemoveButtonClick = async (emailAddress: string) => {
    const result = await sendCommand(() =>
      removeSuppressionList({
        emailAddress: emailAddress,
      }),
    )
    if (result.completed) {
      onRemove()
    }
  }

  const columns: any[] = [
    {
      title: 'Email Address',
      dataIndex: 'emailAddress',
      key: 'emailAddress',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (reason: SuppressionReason) => <ReasonTag reason={reason} />,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (text: string, record: any) => (
        <span>
          <div>{parseZoneFormat(record.createdAt)}</div>
          <div>{formatDateOnlyTime(record.createdAt)}</div>
        </span>
      ),
    },
    {
      align: 'center',
      title: 'Actions',
      dataIndex: 'emailAddress',
      key: 'id',
      sorter: false,
      render: (emailAddress: string, _: any) => (
        <Button
          className={'w-100'}
          onClick={async () => {
            await onRemoveButtonClick(emailAddress)
          }}
          loading={isCommandLoading}
          disabled={isCommandLoading}
        >
          Remove
        </Button>
      ),
    },
  ]

  const onEmailsTableChange = (pagination: any, filters: any, sorter: any) => {
    if (onChange) {
      onChange({
        pageNumber: pagination.current,
        pageSize: pagination.pageSize,
        orderColumn: sorter.field || 'CreatedAt',
        orderDescending: sorter.order !== 'ascend',
      })
    }
  }

  return (
    <Table
      className={'emails-table'}
      dataSource={emails?.items}
      onChange={onEmailsTableChange}
      loading={loadingEmails}
      columns={columns}
      pagination={{
        total: emails?.totalItemCount,
        pageSize: emails?.pageSize,
        current: emails?.pageNumber,
        showSizeChanger: true,
        pageSizeOptions: ['25', '50', '80', '100'],
      }}
      rowKey="id"
    />
  )
}

type SuppressionReasonTagProps = {
  reason: SuppressionReason
}

const ReasonTag = (props: SuppressionReasonTagProps) => {
  switch (props.reason) {
    case SuppressionReason.Bounced:
      return <Tag>Bounced</Tag>
    case SuppressionReason.Complaint:
      return <Tag>Complaint</Tag>
    default:
      return null
  }
}

export default EmailsTable
